import { useEffect, useState } from 'react';
import { call } from './api';
import type { PlexStream } from './plex';

/** Kijkvoorkeuren per profiel (op de server, dus op elk apparaat gelijk). */

export interface Prefs {
  audioLangs: string[];
  subMode: 'off' | 'forced' | 'auto' | 'always';
  subLangs: string[];
  subStyle: { size: 'small' | 'normal' | 'large' | 'xl'; color: 'white' | 'yellow'; background: 'shadow' | 'box' | 'none'; position: 'bottom' | 'raised' };
  autoSkipIntro: boolean;
  nextCountdown: number;
  autoplayNext: boolean;
  loudness: 'off' | 'normal' | 'night';
  spotlightTrailers: boolean;
  cardTrailers: boolean;
  trailerSound: boolean;
  rowInfo: boolean;
  oled: boolean;
  playerView: 'cover' | 'classic' | 'vinyl' | 'lyrics' | 'gradient';
  playerAutoOpen: boolean;
  playerBlur: boolean;
  playerLyrics: boolean;
  playerQuality: boolean;
  playerSpin: boolean;
  playerAccent: string;
  traktScrobble: boolean;
  simklSync: boolean;
  syncBack: boolean;
  traktContinue: boolean;
  screensaverMinutes: number;
  /** Screensaver 2.0: waar de titels vandaan komen en wat je ziet */
  ssSource: 'library' | 'trending' | 'mix';
  ssParts: { info: boolean; clock: boolean; upcoming: boolean; countdown: boolean };
  hideSpoilers: boolean;
  nav: { order: string[]; hidden: string[] } | null;
  sourceOrder: 'plex-first' | 'addons-first' | 'addons-only' | 'plex-only';
  qualityProfile: 'best' | 'balanced' | 'saver';
  hdrPref: 'any' | 'sdr';
  maxSizeGb: number;
  autoPick: boolean;
  failover: boolean;
  prefetchNext: boolean;
  failoverMax: number;
  failoverStall: number;
  failoverSameQuality: boolean;
  failoverCachedOnly: boolean;
  /** Releases beoordelen met TRaSH Guides (zoals Radarr/Sonarr) */
  trashScores: boolean;
  /** Kaart met leeftijd en ouderinfo als een film of aflevering begint */
  ratingCard: boolean;
  /** Titelpagina: beoordelingen, ouderinfo en recensies tonen */
  showRatings: boolean;
  showParentsGuide: boolean;
  showReviews: boolean;
  /** Badge Studio (null = standaard) */
  badges: {
    order: string[];
    hidden: string[];
    style: 'text' | 'color' | 'logo' | 'pack';
    places: { posters: boolean; title: boolean; sources: boolean; player: boolean };
    pack: { name: string; images: Record<string, string> } | null;
  } | null;
  dutchPref: 'off' | 'subs' | 'audio';
  sourcesPerQuality: number;
  /** Woorden waarmee een bron nooit getoond wordt (bijv. CAM, HC, 3D) */
  sourceNever: string[];
  posterLabels: 'skin' | 'off' | 'basic' | 'full';
  posterLabelsPos: 'skin' | 'tl' | 'tr' | 'bl' | 'br';
  posterLabelSize: 'small' | 'normal' | 'large';
  posterScore: boolean;
  posterScorePos: 'auto' | 'tl' | 'tr' | 'bl' | 'br';
  posterLabelStack: 'auto' | 'row' | 'column';
}

export const DEFAULT_PREFS: Prefs = {
  audioLangs: [],
  subMode: 'auto',
  subLangs: ['nld'],
  subStyle: { size: 'normal', color: 'white', background: 'shadow', position: 'bottom' },
  autoSkipIntro: false,
  nextCountdown: 10,
  autoplayNext: true,
  loudness: 'off',
  spotlightTrailers: true,
  cardTrailers: true,
  trailerSound: false,
  rowInfo: true,
  oled: true,
  playerView: 'cover',
  playerAutoOpen: true,
  playerBlur: true,
  playerLyrics: true,
  playerQuality: true,
  playerSpin: true,
  playerAccent: 'auto',
  traktScrobble: false,
  simklSync: false,
  syncBack: false,
  traktContinue: true,
  screensaverMinutes: 10,
  ssSource: 'mix',
  ssParts: { info: true, clock: true, upcoming: true, countdown: true },
  hideSpoilers: false,
  nav: null,
  sourceOrder: 'plex-first',
  qualityProfile: 'best',
  hdrPref: 'any',
  maxSizeGb: 0,
  autoPick: false,
  failover: true,
  prefetchNext: true,
  failoverMax: 5,
  failoverStall: 25,
  failoverSameQuality: false,
  failoverCachedOnly: true,
  trashScores: true,
  ratingCard: true,
  showRatings: true,
  showParentsGuide: true,
  showReviews: true,
  badges: null,
  dutchPref: 'off',
  sourcesPerQuality: 3,
  sourceNever: [],
  posterLabels: 'skin',
  posterLabelsPos: 'skin',
  posterLabelSize: 'normal',
  posterScore: false,
  posterScorePos: 'auto',
  posterLabelStack: 'auto',
};

let cache: Prefs | null = null;
let loading: Promise<Prefs> | null = null;
const listeners = new Set<(p: Prefs) => void>();

export function loadPrefs(force = false): Promise<Prefs> {
  if (cache && !force) return Promise.resolve(cache);
  if (!loading || force) {
    loading = call<{ prefs: Prefs }>('/api/prefs')
      .then((r) => {
        cache = r.prefs;
        listeners.forEach((l) => l(r.prefs));
        return r.prefs;
      })
      .catch(() => cache ?? DEFAULT_PREFS)
      .finally(() => {
        loading = null;
      });
  }
  return loading;
}

export function prefsNow(): Prefs {
  return cache ?? DEFAULT_PREFS;
}

export async function savePrefs(patch: Partial<Prefs>): Promise<Prefs> {
  const r = await call<{ prefs: Prefs }>('/api/prefs', { method: 'PUT', body: JSON.stringify(patch) });
  cache = r.prefs;
  listeners.forEach((l) => l(r.prefs));
  return r.prefs;
}

export function usePrefs(): Prefs {
  const [p, setP] = useState<Prefs>(prefsNow());
  useEffect(() => {
    listeners.add(setP);
    void loadPrefs().then(setP);
    return () => {
      listeners.delete(setP);
    };
  }, []);
  return p;
}

// ---------- Talen ----------

export const LANGUAGES: [string, string][] = [
  ['nld', 'Nederlands'],
  ['eng', 'Engels'],
  ['deu', 'Duits'],
  ['fra', 'Frans'],
  ['spa', 'Spaans'],
  ['ita', 'Italiaans'],
  ['por', 'Portugees'],
  ['jpn', 'Japans'],
  ['kor', 'Koreaans'],
  ['zho', 'Chinees'],
  ['tur', 'Turks'],
  ['ara', 'Arabisch'],
];

/** Plex gebruikt soms de oudere "B"-codes (dut, ger, fre) en soms twee letters. */
const ALIAS: Record<string, string> = {
  dut: 'nld', nl: 'nld', ger: 'deu', de: 'deu', fre: 'fra', fr: 'fra', en: 'eng', es: 'spa', it: 'ita', pt: 'por',
  ja: 'jpn', ko: 'kor', chi: 'zho', zh: 'zho', tr: 'tur', ar: 'ara',
};

export function langOf(s: PlexStream | undefined): string | null {
  const c = (s?.languageCode ?? '').toLowerCase();
  return c ? ALIAS[c] ?? c : null;
}

// ---------- Sporen kiezen ----------

const TEXT = new Set(['srt', 'subrip', 'vtt', 'webvtt', 'ass', 'ssa', 'mov_text']);

/**
 * Welk audio- en ondertitelspoor past bij de voorkeuren?
 * undefined = niets veranderen; null (ondertitels) = uit.
 */
export function pickStreams(streams: PlexStream[], p: Prefs): { audio?: PlexStream; sub?: PlexStream | null } {
  const audios = streams.filter((s) => s.streamType === 2);
  const subs = streams.filter((s) => s.streamType === 3);
  const out: { audio?: PlexStream; sub?: PlexStream | null } = {};

  let audio = audios.find((s) => s.selected) ?? audios[0];
  for (const lang of p.audioLangs) {
    const hit = audios.find((s) => langOf(s) === lang);
    if (hit) {
      audio = hit;
      break;
    }
  }
  if (audio && !audio.selected && audios.length > 1) out.audio = audio;
  const audioLang = langOf(audio);

  // Tekstondertitels gaan voor beeld-ondertitels (PGS): die laatste kosten een transcode
  const rank = (s: PlexStream) => (TEXT.has((s.codec ?? '').toLowerCase()) ? 0 : 1);
  const best = (list: PlexStream[]) => [...list].sort((a, b) => rank(a) - rank(b))[0];
  const inLangs = (forced: boolean) => {
    for (const lang of p.subLangs) {
      const hit = best(subs.filter((s) => langOf(s) === lang && !!s.forced === forced));
      if (hit) return hit;
    }
    return undefined;
  };
  const forcedForAudio = best(subs.filter((s) => s.forced && (!audioLang || langOf(s) === audioLang)));

  let sub: PlexStream | null;
  switch (p.subMode) {
    case 'off':
      sub = null;
      break;
    case 'forced':
      sub = forcedForAudio ?? null;
      break;
    case 'always':
      sub = inLangs(false) ?? inLangs(true) ?? null;
      break;
    case 'auto':
    default: {
      const understands = !!audioLang && p.subLangs.includes(audioLang);
      sub = understands ? forcedForAudio ?? null : inLangs(false) ?? forcedForAudio ?? null;
    }
  }
  const current = subs.find((s) => s.selected) ?? null;
  if ((sub?.id ?? null) !== (current?.id ?? null)) out.sub = sub;
  return out;
}

// ---------- Handmatige keuze per titel onthouden ----------

const MANUAL_KEY = 'tumedia.manualStreams';

/** Heb je bij deze titel zelf een spoor gekozen? Dan laten we de voorkeuren er af. */
export function hasManualChoice(ratingKey: string): boolean {
  try {
    return (JSON.parse(localStorage.getItem(MANUAL_KEY) ?? '[]') as string[]).includes(ratingKey);
  } catch {
    return false;
  }
}

export function markManualChoice(ratingKey: string): void {
  try {
    const list = (JSON.parse(localStorage.getItem(MANUAL_KEY) ?? '[]') as string[]).filter((x) => x !== ratingKey);
    list.push(ratingKey);
    localStorage.setItem(MANUAL_KEY, JSON.stringify(list.slice(-500)));
  } catch {
    // niet erg
  }
}
